import { useEffect, useRef } from 'react';
import { useAuthStore } from '@/store';
import { toast } from 'sonner';

/**
 * useIdleLogout - Kullanıcı 59 dakika boyunca hiçbir işlem yapmazsa
 * otomatik olarak oturumu kapatır ve giriş sayfasına yönlendirir.
 *
 * Dinlenen eventler: mousemove, keydown, click, scroll, touchstart, focus.
 * Cross-tab senkronizasyonu: localStorage anahtarı ile aynı kullanıcının
 * başka sekmedeki aktivitesi de aktivite sayılır.
 */
export function useIdleLogout(timeoutMinutes = 59) {
  const { token, logout } = useAuthStore();
  const timerRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  useEffect(() => {
    if (!token) return; // Sadece giriş yapmış kullanıcılar için

    const TIMEOUT_MS = timeoutMinutes * 60 * 1000;
    const LS_KEY = 'lastActivityAt';

    const reset = () => {
      const now = Date.now();
      lastActivityRef.current = now;
      try { localStorage.setItem(LS_KEY, String(now)); } catch (_) {}
    };

    const check = () => {
      let last = lastActivityRef.current;
      try {
        const stored = parseInt(localStorage.getItem(LS_KEY) || '0', 10);
        if (stored > last) last = stored;
      } catch (_) {}
      const idle = Date.now() - last;
      if (idle >= TIMEOUT_MS) {
        toast.info('Uzun süredir işlem yapmadığınız için oturumunuz kapatıldı.', { duration: 6000 });
        try { logout(); } catch (_) {}
        // Yönlendirme — App.jsx zaten token yoksa login'e yönlendiriyor
        setTimeout(() => {
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
        }, 300);
      }
    };

    // Aktivite eventleri
    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart', 'focus'];
    events.forEach(e => window.addEventListener(e, reset, { passive: true }));

    // Başlangıç
    reset();
    // Her 30 sn'de bir kontrol
    timerRef.current = setInterval(check, 30 * 1000);

    return () => {
      events.forEach(e => window.removeEventListener(e, reset));
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [token, timeoutMinutes, logout]);
}

export default useIdleLogout;
